import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Circle } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

type SubTab = "agents" | "keys";

/**
 * Super Admin -> Print Agent - vidi "Finora - Print Agent API" spec, tačka
 * 8.4: "lista svih agenata po firmama... prinudni opoziv agenta ili
 * ključa". Uključivanje/isključivanje modula print_agent po firmi (i datum
 * isteka) se NAMERNO ne radi ovde - to već pokriva opšti Super Admin
 * "Moduli" tab (Firma -> Moduli), isti mehanizam kao za svaki drugi modul.
 */
export function PrintAgentsAdminPage() {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState<SubTab>("agents");

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>

      <div>
        <h1 className="text-xl font-semibold text-slate-900">Print Agent</h1>
        <p className="text-sm text-slate-500">
          Svi Print Agent uređaji i licencni ključevi, preko svih firmi. Uključivanje/isključivanje modula i
          datum isteka po firmi se podešava u Firma → Moduli.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(
          [
            { key: "agents", label: "Agenti" },
            { key: "keys", label: "Ključevi" },
          ] as { key: SubTab; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              subTab === t.key ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "agents" ? <AgentsTable /> : <KeysTable />}
    </div>
  );
}

function AgentsTable() {
  const queryClient = useQueryClient();
  const { data: agents, isLoading } = useQuery({
    queryKey: ["admin-print-agents"],
    queryFn: () => api.listAllPrintAgentsAdmin(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.adminRevokePrintAgent(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-print-agents"] }),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!agents || agents.length === 0) {
    return <p className="text-sm text-slate-400">Nema registrovanih Print Agent uređaja.</p>;
  }

  return (
    <div className="rounded-xl border border-border bg-surface shadow-subtle">
      <table>
        <thead>
          <tr>
            <th>Firma</th>
            <th>Naziv / hostname</th>
            <th>Verzija</th>
            <th>Poslednje javljanje</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a.id}>
              <td className="font-medium text-slate-800">{a.company_name}</td>
              <td>
                <div className="flex items-center gap-2">
                  <Printer className="h-4 w-4 text-slate-300" aria-hidden />
                  <div>
                    <div className="text-sm text-slate-700">{a.name}</div>
                    <div className="text-xs text-slate-400">{a.hostname}</div>
                  </div>
                </div>
              </td>
              <td className="text-sm text-slate-500">{a.version}</td>
              <td className="text-sm text-slate-500">{new Date(a.last_seen_at).toLocaleString("sr-RS")}</td>
              <td>
                {a.revoked ? (
                  <span className="text-xs text-slate-400">Opozvan</span>
                ) : (
                  <span className="flex items-center gap-1 text-xs">
                    <Circle
                      className={`h-2 w-2 ${a.online ? "fill-green-500 text-green-500" : "fill-slate-300 text-slate-300"}`}
                      aria-hidden
                    />
                    {a.online ? "Online" : "Offline"}
                  </span>
                )}
              </td>
              <td>
                {!a.revoked && (
                  <Button
                    variant="secondary"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      if (confirm(`Opozvati agenta "${a.name}" (${a.company_name})?`)) revoke.mutate(a.id);
                    }}
                  >
                    Opozovi
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeysTable() {
  const queryClient = useQueryClient();
  const { data: keys, isLoading } = useQuery({
    queryKey: ["admin-print-agent-keys"],
    queryFn: () => api.listAllPrintAgentKeysAdmin(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.adminRevokePrintAgentKey(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-print-agent-keys"] }),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!keys || keys.length === 0) {
    return <p className="text-sm text-slate-400">Nema kreiranih licencnih ključeva.</p>;
  }

  return (
    <div className="rounded-xl border border-border bg-surface shadow-subtle">
      <table>
        <thead>
          <tr>
            <th>Firma</th>
            <th>Oznaka</th>
            <th>Prefiks</th>
            <th>Poslednje korišćen</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id}>
              <td className="font-medium text-slate-800">{k.company_name}</td>
              <td className="text-sm text-slate-500">{k.label ?? "—"}</td>
              <td className="font-mono text-xs text-slate-500">{k.key_prefix}…</td>
              <td className="text-sm text-slate-500">
                {k.last_used_at ? new Date(k.last_used_at).toLocaleString("sr-RS") : "—"}
              </td>
              <td className="text-xs text-slate-400">{k.revoked_at ? "Opozvan" : "Aktivan"}</td>
              <td>
                {!k.revoked_at && (
                  <Button
                    variant="secondary"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      if (confirm(`Opozvati ključ "${k.label ?? k.key_prefix}" (${k.company_name})?`))
                        revoke.mutate(k.id);
                    }}
                  >
                    Opozovi
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
